import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { LegalLinks } from "@/components/LegalDialog";
import { useAuth } from "@/contexts/AuthContext";

const EXPIRED_MSG = "Your session expired — please sign back in.";

export default function Login() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const expired = typeof window !== "undefined" && sessionStorage.getItem("sd:auth_expired") === "1";
  const [error] = useState(location.state?.error || (expired ? EXPIRED_MSG : ""));

  // A session that lapsed mid-use bounces here — greet it kindly instead of silently.
  useEffect(() => {
    if (expired) {
      sessionStorage.removeItem("sd:auth_expired");
      // Defer so the global Toaster is subscribed before we push the toast.
      const t = setTimeout(() => toast.warning("Session expired", { description: "Please sign back in to continue.", duration: 6000 }), 60);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Already logged in → go to the app.
  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const signInWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      {/* Plaid backdrop + adaptive scrim (Stage Door tartan identity) */}
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(/logo.jpg)" }} />
      <div className="absolute inset-0 plaid-veil" />

      <div className="relative flex min-h-screen items-center justify-center px-5 py-10">
        <div className="w-full max-w-sm" data-testid="login-splash">
          <div className="mb-8 flex flex-col items-center text-center">
            <img
              src="/icon-512.png"
              alt="Stage Door"
              draggable={false}
              className="mb-4 h-24 w-24 select-none rounded-[22px] shadow-2xl ring-1 ring-white/15"
              data-testid="login-logo"
            />
            <div className="inline-flex items-center rounded-xl bg-[#9B2226] px-4 py-2 shadow-lg ring-1 ring-black/15" data-testid="login-brand">
              <span className="font-700 text-xs uppercase tracking-[0.18em] text-white">VIP All-In-One</span>
            </div>
          </div>

          <div className="lg-glass-strong rounded-3xl p-6">
            <h2 className="mb-1 font-display text-lg font-700 tracking-tight text-zinc-100">Welcome back</h2>
            <p className="mb-5 text-sm text-zinc-300">Sign in with a personal Google account to open your dashboard.</p>

            {error && (
              <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="auth-error">
                {error}
              </p>
            )}

            <Button
              onClick={signInWithGoogle}
              className="h-12 w-full gap-3 bg-white text-base font-600 text-zinc-800 hover:bg-zinc-100"
              data-testid="google-login-button"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </Button>
          </div>

          <p className="mt-6 text-center text-xs text-zinc-300">You'll stay signed in for about a week.</p>
          <p className="mt-3 text-center text-[11px] text-zinc-400">
            By continuing you agree to our <LegalLinks linkClassName="font-600 text-zinc-200 underline hover:text-white" />.
          </p>
          <p className="mt-3 text-center text-[11px] leading-relaxed text-zinc-400" data-testid="disney-disclaimer">
            Stage Door is an independent personal tool and is not affiliated with, endorsed by, or associated with The Walt Disney Company.
          </p>
        </div>
      </div>
    </div>
  );
}
